// Explicador "¿Qué es un gancho?" del modo "Video con influencer". Se abre al
// tocar el link/botón "?" debajo del selector de gancho.
//
// Mobile: bottom sheet con drag handle. Desktop: panel lateral a la derecha.
// Mismo chrome que `VideoConfigSheet`.
//
// El video de ejemplo reproduce los clips de los DISTINTOS ganchos
// (`video_hooks`) uno después del otro, en loop (ADR-556). Si la tabla no
// tiene videos, cae al placeholder.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Film, X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';
import { fetchVideoHooks, type GanchoOption } from '../ganchosCatalog';

export interface GanchoExplainerSheetProps {
  open: boolean;
  onClose: () => void;
}

export const GanchoExplainerSheet: React.FC<GanchoExplainerSheetProps> = ({
  open,
  onClose,
}) => {
  const { mounted, animated } = useExitTransition(open, 300);
  // Playlist: solo ganchos con video. Fetch lazy en la primera apertura.
  const [ganchos, setGanchos] = useState<GanchoOption[]>([]);
  const [idx, setIdx] = useState(0);
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    void fetchVideoHooks()
      .then((rows) => setGanchos(rows.filter((g) => !!g.videoUrl)))
      .catch((err) => {
        // Sin catch, un reject dejaba fetchedRef en true y el sheet mostraba
        // el placeholder para siempre; reseteamos para reintentar al reabrir.
        console.error('[GanchoExplainerSheet] fetchVideoHooks falló', err);
        fetchedRef.current = false;
      });
  }, [open]);
  // Al reabrir, arrancar siempre desde el primer gancho.
  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  if (!mounted) return null;

  const current = ganchos.length > 0 ? ganchos[idx % ganchos.length] : null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Mobile drag handle */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity md:hidden"
          >
            Cerrar
          </button>
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black/90 md:flex-1">
            ¿Qué es un gancho?
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Contenido. En DESKTOP el panel lateral es full-height: el video
            crece para aprovechar todo el espacio vertical disponible
            (flex-col sin scroll, video flex-1 con aspect fijo → el ancho lo
            da la altura). En mobile queda compacto como siempre. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 space-y-5 md:flex md:flex-col md:overflow-hidden">
          <p className="text-[14px] leading-relaxed text-[#3C3C43]/75 md:shrink-0">
            Un <span className="font-semibold text-brand-black/90">gancho</span>{' '}
            es un clip corto en vertical que se coloca al inicio del video para
            captar la atención en los primeros segundos. Es lo que hace que
            alguien deje de scrollear y se quede mirando tu anuncio.
          </p>

          {/* Video vertical de ejemplo: los clips de los distintos ganchos,
              uno tras otro en loop. `key` fuerza el remount al cambiar de
              clip (recarga + autoplay); con un solo gancho, loop nativo.
              aspectRatio inline: `aspect-[9/16]` no compila en este setup
              de Tailwind (ADR-538). */}
          {current ? (
            <div className="space-y-2 md:flex-1 md:min-h-0 md:flex md:flex-col md:items-center">
              <video
                key={current.id}
                src={current.videoUrl}
                poster={current.posterUrl || undefined}
                autoPlay
                muted
                playsInline
                preload="auto"
                loop={ganchos.length === 1}
                onEnded={() => setIdx((i) => (i + 1) % ganchos.length)}
                className="mx-auto md:mx-0 w-[180px] md:w-auto md:flex-1 md:min-h-0 rounded-2xl object-cover bg-neutral-100"
                style={{ aspectRatio: '9 / 16' }}
              />
              <p className="text-center text-[12px] text-[#3C3C43]/50 md:shrink-0">
                {current.nombre}
              </p>
            </div>
          ) : (
            <div className="md:flex-1 md:min-h-0 md:flex md:flex-col md:items-center">
              <div
                className="mx-auto md:mx-0 w-[180px] md:w-auto md:h-full rounded-2xl bg-neutral-100 flex flex-col items-center justify-center gap-2 text-neutral-400"
                style={{ aspectRatio: '9 / 16' }}
              >
                <Film className="w-8 h-8" strokeWidth={1.5} />
                <span className="text-[11px] font-medium">Video de ejemplo</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};
